import Flash from "@shared/services/flashv2.js";
import { SalesRepsEventEntity } from "@shared/services/event_tracking/entities";
import type { Distributor } from "@shared/types/Distributor";
import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import { EventTracking } from "@shared/services/event_tracking/events";
import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

export default function AddRepModalService() {}

AddRepModalService.prototype.errorCallback = function (res: any) {
  Flash.now({ type: "error", message: res.data?.errors });
};

AddRepModalService.prototype.addSalesRepErrorCallback = function (res: any) {
  Flash.now({ type: "error", message: res.data?.errors?.messages[0] });
};

const { trackEvent } = EventTracking;

let repsUpdatedMessage = (distributor: Distributor, reps) => {
  let addedMessage = "";
  let removedMessage = "";
  if (reps.added.length > 0)
    addedMessage = `${reps.added.join(", ")} added to ${distributor.name}. `;
  if (reps.removed.length > 0)
    removedMessage = `${reps.removed.join(", ")} removed from ${distributor.name}.`;
  return addedMessage + removedMessage;
};

type SubmitFn = (distributor: Distributor, reps: [], cb: any) => void;
AddRepModalService.prototype.submit = function (
  callback: (distributor: Distributor, addedReps: string[]) => void,
): SubmitFn {
  const doSubmit = async (distributor, reps, _errorCallback) => {
    try {
      const res = await HttpPromise({
        method: "POST",
        url: "/api/retailer/distributors/" + distributor.id + "/sales_rep_assignments/replace",
        data: {
          replace: {
            sales_rep_ids: reps.map(r => r.id),
          },
        },
      });

      let header;
      let msg;

      if (!distributor.sales_reps || distributor.sales_reps.length == 0) {
        header = "Distributor connected";
        msg = `You can now place orders with ${distributor.name}. Manage your sales reps in Settings → My Distributors`;
      } else {
        msg = repsUpdatedMessage(distributor, res);
        header = "Sales reps updated";
        trackEvent(
          "Sales Reps Added",
          new SalesRepsEventEntity({
            service: "Retailer",
            component: "Distributor Settings",
            ids: distributor.sales_reps.map(rep => rep.id),
          }),
        );
      }

      Flash.now({ type: "success", message: msg, timeout: 10000, header });
      DistributorsStore.pushAddedSalesReps(distributor.id, reps);
      if (callback) {
        callback(distributor, res.added);
      }
    } catch (error) {
      console.log("error", error);
      Flash.now({ type: "error", message: error });
    }
  };
  return doSubmit;
};

AddRepModalService.prototype.addNewSalesRep = async (distributor, newRep, errorCallback) => {
  try {
    let res = await HttpPromise({
      method: "POST",
      url: "/api/retailer/distributors/" + distributor.id + "/sales_reps",
      data: {
        sales_rep: {
          name: newRep.name,
          contact_phone: newRep.contact_phone,
          contact_email: newRep.contact_email,
          skip_assignment: true,
        },
      },
    });
    trackEvent(
      "Sales Reps Added",
      new SalesRepsEventEntity({
        service: "Retailer",
        component: "Distributor Settings New Sales Rep",
        id: res.id,
      }),
    );
    return res;
  } catch (e) {
    console.warn(e);
    Flash.now({
      type: "error",
      message: "There was a problem adding your sales rep",
    });
  }
};

AddRepModalService.prototype.connectAccount = async (
  distributor: { id: number; name: string },
  accountNumber: string,
  salesRepIds: number[],
) => {
  try {
    const response = await HttpPromise({
      url: `/api/retailer/distributors/${distributor.id}/distributor_accounts/connect_account`,
      method: "POST",
      data: {
        account_number: accountNumber,
        sales_rep_ids: salesRepIds,
      },
    });

    DistributorsStore.updateDistributor(distributor.id, {
      sales_reps: response.sales_rep_assignments,
      account_number: response.account_number,
    });
    Flash.now({
      type: "success",
      message:
        distributor.id === VT_802_SPIRITS_DISTRIBUTOR_ID
          ? `Your account was successfully connected to ${distributor.name}`
          : `Your account number for ${distributor.name} was successfully added`,
    });
    return response;
  } catch (e) {
    console.warn(e);
    Flash.now({
      type: "error",
      message:
        distributor.id === VT_802_SPIRITS_DISTRIBUTOR_ID
          ? "We were unable to connect your account. Please try again."
          : "We were unable to add your account number. Please try again.",
    });
    throw e;
  }
};
